// Minigames retain their partial results and phase when the panel closes.
import { newAttempt, type InteractiveAttempt } from '../core/TaskAttempt';

interface PersistentOptions { attempt?: InteractiveAttempt; onProgress?: () => void; }

export interface MinigameHandle {
  destroy(): void;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function isSpace(e: KeyboardEvent): boolean {
  return e.code === 'Space' && !e.repeat;
}

// ---------------------------------------------------------------- timing bar

export interface TimingResult {
  score: number;
  hits: number;
  misses: number;
}

export interface TimingOpts extends PersistentOptions {
  pulls: number;
  /** Green zone width as a fraction of the bar (already scaled by tolerance) */
  zoneWidth: number;
  periodMs: number;
  buttonLabel: string;
  hint: string;
  onDone: (r: TimingResult) => void;
}

/** A marker sweeps back and forth. Stop it inside the zone. */
export function timingBar(container: HTMLElement, opts: TimingOpts): MinigameHandle {
  const a = opts.attempt ?? newAttempt('timing');
  if (!Object.keys(a.parameters).length) a.parameters = { zoneWidth: opts.zoneWidth, pulls: opts.pulls, periodMs: opts.periodMs };
  opts = { ...opts, ...a.parameters };
  const el = document.createElement('div');
  el.className = 'mg';
  el.innerHTML = `
    <div class="muted"></div>
    <div class="mg-bar"><div class="mg-zone"></div><div class="mg-marker"></div></div>
    <div class="mg-marks"></div>
    <div class="row"><button class="btn primary mg-btn"></button><span class="mg-status"></span></div>`;
  container.appendChild(el);
  (el.querySelector('.muted') as HTMLElement).textContent = opts.hint;
  const zone = el.querySelector('.mg-zone') as HTMLElement;
  const marker = el.querySelector('.mg-marker') as HTMLElement;
  const marks = el.querySelector('.mg-marks') as HTMLElement;
  const btn = el.querySelector('.mg-btn') as HTMLButtonElement;
  const status = el.querySelector('.mg-status') as HTMLElement;
  btn.innerHTML = `${opts.buttonLabel} <span class="kbd">Space</span>`;
  for (let i = 0; i < opts.pulls; i++) marks.appendChild(document.createElement('span'));

  const zw = clamp(opts.zoneWidth, 0.05, 0.8);
  let pull = a.pull;
  let hits = a.hits;
  let misses = a.misses;
  let scoreSum = a.scoreSum;
  let center = a.center;
  let t0 = performance.now() - a.elapsed;
  let raf = 0;
  let done = a.completed;
  const snapshot = (): void => {
    Object.assign(a, { pull, hits, misses, scoreSum, center, elapsed: performance.now() - t0, completed: done });
  };

  const newZone = (): void => {
    center = zw / 2 + Math.random() * (1 - zw);
    zone.style.left = `${(center - zw / 2) * 100}%`;
    zone.style.width = `${zw * 100}%`;
    status.textContent = `Pull ${pull + 1} of ${opts.pulls}`;
    t0 = performance.now() - Math.random() * opts.periodMs;
  };
  const pos = (now: number): number => (1 - Math.cos((2 * Math.PI * (now - t0)) / opts.periodMs)) / 2;
  const frame = (now: number): void => {
    if (done) return;
    snapshot();
    marker.style.left = `${pos(now) * 100}%`;
    raf = requestAnimationFrame(frame);
  };
  const finish = (): void => {
    done = true;
    cancelAnimationFrame(raf);
    window.removeEventListener('keydown', onKey);
    btn.disabled = true;
    status.textContent = `${hits}/${opts.pulls} clean`;
    snapshot();
    opts.onDone({ score: scoreSum / opts.pulls, hits, misses });
  };
  const act = (): void => {
    if (done) return;
    const p = pos(performance.now());
    const dist = Math.abs(p - center);
    const half = zw / 2;
    const q = dist <= half ? 1 : Math.max(0, 1 - (dist - half) / half);
    if (q >= 1) hits++;
    else misses++;
    scoreSum += q;
    (marks.children[pull] as HTMLElement).className = q >= 1 ? 'hit' : 'miss';
    a.claimed[pull] = q >= 1 ? 'hit' : 'miss';
    pull++;
    if (pull >= opts.pulls) finish();
    else newZone();
    snapshot();
    opts.onProgress?.();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (isSpace(e)) {
      e.preventDefault();
      act();
      snapshot();
      opts.onProgress?.();
    }
  };
  btn.onclick = act;
  window.addEventListener('keydown', onKey);
  for (let i = 0; i < pull; i++) (marks.children[i] as HTMLElement).className = a.claimed[i] ?? 'miss';
  if (!pull && !a.elapsed) newZone();
  else {
    zone.style.left = `${(center - zw / 2) * 100}%`;
    zone.style.width = `${zw * 100}%`;
    status.textContent = `Pull ${pull + 1} of ${opts.pulls}`;
  }
  raf = requestAnimationFrame(frame);

  return {
    destroy() {
      snapshot();
      done = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('keydown', onKey);
      opts.onProgress?.();
      el.remove();
    },
  };
}

// ---------------------------------------------------------------- tempo stir

export interface TempoResult {
  hits: number;
  misses: number;
  early: number;
  late: number;
}

export interface TempoOpts extends PersistentOptions {
  beats: number;
  beatMs: number;
  toleranceMs: number;
  buttonLabel: string;
  hint: string;
  onDone: (r: TempoResult) => void;
}

/** Click on the beat. Early clicks and double clicks count as "too fast". */
export function tempoStir(container: HTMLElement, opts: TempoOpts): MinigameHandle {
  const a = opts.attempt ?? newAttempt('tempo');
  if (!Object.keys(a.parameters).length) a.parameters = { beats: opts.beats, beatMs: opts.beatMs, toleranceMs: Math.min(opts.toleranceMs, opts.beatMs * .4) };
  opts = { ...opts, ...a.parameters };
  const el = document.createElement('div');
  el.className = 'mg';
  el.innerHTML = `
    <div class="muted"></div>
    <div class="mg-pulse"></div>
    <div class="mg-marks"></div>
    <div class="row"><button class="btn primary mg-btn"></button><span class="mg-status">Get ready…</span></div>`;
  container.appendChild(el);
  (el.querySelector('.muted') as HTMLElement).textContent = opts.hint;
  const pulse = el.querySelector('.mg-pulse') as HTMLElement;
  const marks = el.querySelector('.mg-marks') as HTMLElement;
  const btn = el.querySelector('.mg-btn') as HTMLButtonElement;
  const status = el.querySelector('.mg-status') as HTMLElement;
  btn.innerHTML = `${opts.buttonLabel} <span class="kbd">Space</span>`;
  for (let i = 0; i < opts.beats; i++) marks.appendChild(document.createElement('span'));

  const leadIn = 1600;
  const t0 = performance.now() + leadIn - a.elapsed;
  const claimed: (null | 'hit' | 'miss')[] = a.claimed.length ? a.claimed : Array(opts.beats).fill(null);
  let hits = a.hits;
  let early = a.early;
  let late = a.late;
  let done = a.completed;
  const snapshot = (): void => { Object.assign(a, { hits, early, late, claimed, elapsed: performance.now() - t0 + leadIn, completed: done }); };
  let shownBeat = -1;
  let raf = 0;
  let pulseTimer = 0;
  let finishTimer = 0;

  const paint = (): void => {
    claimed.forEach((c, i) => {
      (marks.children[i] as HTMLElement).className = c ?? '';
    });
  };
  const finish = (): void => {
    if (done) return;
    done = true;
    cancelAnimationFrame(raf);
    clearTimeout(pulseTimer);
    clearTimeout(finishTimer);
    window.removeEventListener('keydown', onKey);
    btn.disabled = true;
    claimed.forEach((c, i) => {
      if (!c) {
        claimed[i] = 'miss';
        late++;
      }
    });
    paint();
    status.textContent = `${hits}/${opts.beats} on tempo`;
    snapshot();
    opts.onDone({ hits, misses: early + late, early, late });
  };
  const frame = (now: number): void => {
    if (done) return;
    snapshot();
    if (now < t0) {
      status.textContent = `Ready in ${Math.ceil((t0 - now) / 1000)}…`;
    } else {
      const k = Math.floor((now - t0) / opts.beatMs);
      if (k !== shownBeat && k >= 0 && k < opts.beats) {
        shownBeat = k;
        pulse.classList.add('beat');
        clearTimeout(pulseTimer);
        pulseTimer = window.setTimeout(() => pulse.classList.remove('beat'), 130);
        status.textContent = `Beat ${k + 1} of ${opts.beats}`;
      }
      if (now > t0 + opts.beats * opts.beatMs + opts.toleranceMs + 80) {
        finish();
        return;
      }
    }
    raf = requestAnimationFrame(frame);
  };
  const act = (): void => {
    if (done) return;
    const now = performance.now();
    if (now < t0 - opts.toleranceMs) {
      early++;
      status.textContent = 'Too early!';
      return;
    }
    const k = Math.round((now - t0) / opts.beatMs);
    if (k < 0 || k >= opts.beats) return;
    const offset = now - (t0 + k * opts.beatMs);
    if (claimed[k]) {
      early++;
      status.textContent = 'Too fast, easy…';
      return;
    }
    if (Math.abs(offset) <= opts.toleranceMs) {
      claimed[k] = 'hit';
      hits++;
    } else {
      claimed[k] = 'miss';
      if (offset < 0) early++;
      else late++;
    }
    paint();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (isSpace(e)) {
      e.preventDefault();
      act();
      snapshot();
      opts.onProgress?.();
    }
  };
  btn.onclick = () => { act(); snapshot(); opts.onProgress?.(); };
  window.addEventListener('keydown', onKey);
  raf = requestAnimationFrame(frame);
  // Deadline timer so the round ends even when animation frames are paused (hidden tab)
  paint();
  finishTimer = window.setTimeout(finish, Math.max(0, leadIn + opts.beats * opts.beatMs + opts.toleranceMs + 150 - a.elapsed));

  return {
    destroy() {
      snapshot();
      done = true;
      cancelAnimationFrame(raf);
      clearTimeout(pulseTimer);
      clearTimeout(finishTimer);
      window.removeEventListener('keydown', onKey);
      opts.onProgress?.();
      el.remove();
    },
  };
}

// ---------------------------------------------------------------- hold & release

export interface HoldStretchOpts extends PersistentOptions {
  pulls: number;
  zoneStart: number;
  zoneEnd: number;
  fillMs: number;
  buttonLabel: string;
  hint: string;
  onDone: (r: { hits: number; misses: number }) => void;
}

/** Hold to fill a bar, release inside the zone. Filling to the end tears the curd. */
export function holdStretch(container: HTMLElement, opts: HoldStretchOpts): MinigameHandle {
  const a = opts.attempt ?? newAttempt('stretch');
  if (!Object.keys(a.parameters).length) a.parameters = { pulls: opts.pulls, zoneStart: opts.zoneStart, zoneEnd: opts.zoneEnd, fillMs: opts.fillMs };
  opts = { ...opts, ...a.parameters };
  const el = document.createElement('div');
  el.className = 'mg';
  el.innerHTML = `
    <div class="muted"></div>
    <div class="mg-bar"><div class="mg-zone"></div><div class="mg-fill"></div></div>
    <div class="mg-marks"></div>
    <div class="row"><button class="btn primary mg-btn"></button><span class="mg-status"></span></div>`;
  container.appendChild(el);
  (el.querySelector('.muted') as HTMLElement).textContent = opts.hint;
  const zone = el.querySelector('.mg-zone') as HTMLElement;
  const fill = el.querySelector('.mg-fill') as HTMLElement;
  const marks = el.querySelector('.mg-marks') as HTMLElement;
  const btn = el.querySelector('.mg-btn') as HTMLButtonElement;
  const status = el.querySelector('.mg-status') as HTMLElement;
  btn.innerHTML = `Hold: ${opts.buttonLabel} <span class="kbd">Space</span>`;
  for (let i = 0; i < opts.pulls; i++) marks.appendChild(document.createElement('span'));
  const zs = clamp(opts.zoneStart, 0.05, 0.95);
  const ze = Math.min(clamp(opts.zoneEnd, zs + 0.03, 0.98), zs + .8);
  zone.style.left = `${zs * 100}%`;
  zone.style.width = `${(ze - zs) * 100}%`;

  let holding = false;
  let holdStart = 0;
  let pull = a.pull;
  let hits = a.hits;
  let misses = a.misses;
  let done = a.completed;
  const snapshot = (): void => { Object.assign(a, { pull, hits, misses, completed: done }); };
  let raf = 0;
  let tearTimer = 0;
  status.textContent = `Stretch ${pull + 1} of ${opts.pulls}`;
  a.claimed.forEach((c, i) => { if (marks.children[i]) (marks.children[i] as HTMLElement).className = c ?? ''; });

  const finish = (): void => {
    done = true;
    cancelAnimationFrame(raf);
    clearTimeout(tearTimer);
    detach();
    btn.disabled = true;
    status.textContent = `${hits}/${opts.pulls} clean`;
    snapshot();
    opts.onDone({ hits, misses });
  };
  const release = (p: number): void => {
    if (!holding || done) return;
    holding = false;
    clearTimeout(tearTimer);
    const hit = p >= zs && p <= ze && p < 1;
    if (hit) hits++;
    else misses++;
    (marks.children[pull] as HTMLElement).className = hit ? 'hit' : 'miss';
    a.claimed[pull] = hit ? 'hit' : 'miss';
    fill.style.width = '0%';
    pull++;
    if (pull >= opts.pulls) finish();
    else status.textContent = `Stretch ${pull + 1} of ${opts.pulls}`;
    snapshot();
    opts.onProgress?.();
  };
  const frame = (now: number): void => {
    if (done) return;
    if (holding) {
      const p = Math.min(1, (now - holdStart) / opts.fillMs);
      fill.style.width = `${p * 100}%`;
      if (p >= 1) release(1);
    }
    if (!done) raf = requestAnimationFrame(frame);
  };
  const down = (): void => {
    if (done || holding) return;
    holding = true;
    holdStart = performance.now();
    // Tear deadline independent of animation frames
    tearTimer = window.setTimeout(() => release(1), opts.fillMs + 5);
  };
  const up = (): void => {
    if (holding) release(Math.min(1, (performance.now() - holdStart) / opts.fillMs));
  };
  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.code === 'Space') {
      e.preventDefault();
      if (!e.repeat) down();
    }
  };
  const onKeyUp = (e: KeyboardEvent): void => {
    if (e.code === 'Space') {
      e.preventDefault();
      up();
    }
  };
  btn.addEventListener('pointerdown', down);
  window.addEventListener('pointerup', up);
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  const detach = (): void => {
    btn.removeEventListener('pointerdown', down);
    window.removeEventListener('pointerup', up);
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
  };
  raf = requestAnimationFrame(frame);

  return {
    destroy() {
      // Leaving while holding releases at the real phase, preserving this pull.
      if (holding && !done) up();
      snapshot();
      done = true;
      cancelAnimationFrame(raf);
      clearTimeout(tearTimer);
      detach();
      opts.onProgress?.();
      el.remove();
    },
  };
}

// ---------------------------------------------------------------- hold to complete

export interface HoldOpts extends PersistentOptions {
  ms: number;
  buttonLabel: string;
  hint: string;
  onDone: () => void;
}

/** Hold the button for a duration. Releasing early resets. */
export function holdButton(container: HTMLElement, opts: HoldOpts): MinigameHandle {
  const a = opts.attempt ?? newAttempt('hold');
  if (!Object.keys(a.parameters).length) a.parameters = { ms: opts.ms };
  opts = { ...opts, ...a.parameters };
  const el = document.createElement('div');
  el.className = 'mg';
  el.innerHTML = `
    <div class="muted"></div>
    <div class="mg-bar"><div class="mg-fill"></div></div>
    <div class="row"><button class="btn primary mg-btn"></button><span class="mg-status"></span></div>`;
  container.appendChild(el);
  (el.querySelector('.muted') as HTMLElement).textContent = opts.hint;
  const fill = el.querySelector('.mg-fill') as HTMLElement;
  const btn = el.querySelector('.mg-btn') as HTMLButtonElement;
  const status = el.querySelector('.mg-status') as HTMLElement;
  btn.innerHTML = `Hold: ${opts.buttonLabel} <span class="kbd">Space</span>`;

  let holding = false;
  let start = 0;
  let done = a.completed;
  let raf = 0;
  let completeTimer = 0;
  fill.style.width = `${Math.min(100, a.elapsed / opts.ms * 100)}%`;
  status.textContent = `${Math.round(Math.min(100, a.elapsed / opts.ms * 100))}%`;
  const complete = (): void => {
    if (done) return;
    done = true;
    a.completed = true;
    a.elapsed = opts.ms;
    cancelAnimationFrame(raf);
    clearTimeout(completeTimer);
    detach();
    btn.disabled = true;
    fill.style.width = '100%';
    status.textContent = '100%';
    opts.onDone();
  };
  const frame = (now: number): void => {
    if (done) return;
    if (holding) {
      const p = Math.min(1, (now - start) / opts.ms);
      a.elapsed = now - start;
      fill.style.width = `${p * 100}%`;
      status.textContent = `${Math.round(p * 100)}%`;
      if (p >= 1) {
        complete();
        return;
      }
    }
    raf = requestAnimationFrame(frame);
  };
  const down = (): void => {
    if (done || holding) return;
    holding = true;
    start = performance.now() - a.elapsed;
    completeTimer = window.setTimeout(complete, Math.max(0, opts.ms - a.elapsed + 5));
  };
  const up = (): void => {
    if (!holding || done) return;
    holding = false;
    a.elapsed = Math.min(opts.ms, performance.now() - start);
    clearTimeout(completeTimer);
    fill.style.width = `${a.elapsed / opts.ms * 100}%`;
    status.textContent = 'Progress saved. Hold to continue…';
    opts.onProgress?.();
  };
  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.code === 'Space') {
      e.preventDefault();
      if (!e.repeat) down();
    }
  };
  const onKeyUp = (e: KeyboardEvent): void => {
    if (e.code === 'Space') {
      e.preventDefault();
      up();
    }
  };
  btn.addEventListener('pointerdown', down);
  window.addEventListener('pointerup', up);
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  const detach = (): void => {
    btn.removeEventListener('pointerdown', down);
    window.removeEventListener('pointerup', up);
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
  };
  raf = requestAnimationFrame(frame);

  return {
    destroy() {
      if (holding && !done) up();
      done = true;
      cancelAnimationFrame(raf);
      clearTimeout(completeTimer);
      detach();
      opts.onProgress?.();
      el.remove();
    },
  };
}
